import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { uniqBy } from "lodash";
import {
  ALLOWED_ACTIVITY_EVENTS,
  ALLOWED_SPIFF_EVENTS,
  getSpiffEventName,
} from "@/activity-feed/constants";
import { useApi } from "@/core/api";
import type { ICustomer } from "@/customer/customer";
import { getEnrichedCustomer } from "@/customer/utils";
import type { IReferral } from "@/referrals/referral";
import { getEnrichedReferral } from "@/referrals/utils";
import { businessReviewsKey, reviewKey } from "@/reviews/query";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import type { PhotoTrigger } from "@/spiffs/spiffs.d";
import { useGetMyUser } from "@/user/query";
import type {
  ActivityEventName,
  IActivityFeedItem,
  IActivityFeedResponse,
  IEnrichedActivityFeedItem,
  IPricebookItem,
} from "./activity-feed.d";
import type { ActivityFeedQueryFilters } from "./query-utils";
import {
  normalizeActivityFeedQueryFilters,
  type NormalizedActivityFeedQueryFilters,
} from "./query-utils";
import { enrichActivityFeedItemForUser } from "./utils";

export const activityFeedKey = (businessId?: string | null) =>
  ["activity-feed", businessId] as const;

const ACTIVITY_FEED_STALE_TIME_MS = 1 * 60 * 1000;

interface IActivityFeedEventOption {
  event_name: string;
  count: number;
}

type IEnrichedActivityFeedResponse =
  IActivityFeedResponse<IEnrichedActivityFeedItem>;

const activityFeedItemsEndpoint = (businessId: string) =>
  `businesses/${businessId}/activity_feed_items`;

const activityFeedEventsEndpoint = (businessId: string) =>
  `businesses/${businessId}/activity_feed_events`;

export const activityFeedInfiniteKey = (
  businessId?: string | null,
  normalizedFilters: NormalizedActivityFeedQueryFilters = normalizeActivityFeedQueryFilters(),
) => [...activityFeedKey(businessId), normalizedFilters] as const;

interface IActivityFeedRequestParamsOptions {
  myActivity?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  showUnattributedReviews?: boolean;
  selectedFilters?: string[];
}

const buildActivityFeedRequestParams = ({
  myActivity,
  page,
  pageSize,
  search,
  showUnattributedReviews,
  selectedFilters,
}: IActivityFeedRequestParamsOptions = {}) => {
  const searchParams = new URLSearchParams();

  if (page !== undefined) {
    searchParams.set("page", String(page));
  }

  if (pageSize !== undefined) {
    searchParams.set("page_size", String(pageSize));
  }

  if (myActivity) {
    searchParams.set("my_activity", "true");
  }

  if (search) {
    searchParams.set("search", search);
  }

  if (showUnattributedReviews) {
    searchParams.set("unattributed_reviews", "true");
  } else if (selectedFilters?.length) {
    selectedFilters.forEach((eventName) => {
      searchParams.append("spiff_event_names[]", eventName);
    });
  }

  return searchParams;
};

const getEnrichedActivityFeedItems = (
  items: IActivityFeedItem[],
  currentUser: ReturnType<typeof useGetMyUser>["data"],
) => {
  const filteredItems = items
    // Filter to only allowed raw event names from API
    .filter((item) => ALLOWED_ACTIVITY_EVENTS.has(item.event_name))
    .map((item) => enrichActivityFeedItemForUser(item, currentUser))
    .filter((item) => ALLOWED_SPIFF_EVENTS.has(item.spiff_event_name));

  // The API may emit both `review` and `five_star_review` for the same review row.
  // Deduping by normalized event + record identity keeps the feed stable.
  return uniqBy(filteredItems, (item) =>
    [
      item.underlying_record_type,
      item.underlying_record_id,
      item.timestamp,
      item.spiff_event_name,
    ].join("-"),
  );
};

export const EVENT_TO_RECORD_TYPE_MAP = {
  [SPIFF_RULE_EVENTS.signup]: "customer",
  [SPIFF_RULE_EVENTS.referral_creation]: "referral",
  [SPIFF_RULE_EVENTS.referral_completion]: "referral",
  [SPIFF_RULE_EVENTS.five_star_review]: "review",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "photo",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "pricebook_item",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "placeholder",
  [SPIFF_RULE_EVENTS.driving_score]: "placeholder",
  [SPIFF_RULE_EVENTS.generic]: "placeholder",
} as const;

export type RecordType =
  (typeof EVENT_TO_RECORD_TYPE_MAP)[keyof typeof EVENT_TO_RECORD_TYPE_MAP];

export const activityCustomerKey = (customerId: string) =>
  ["activity-customer", customerId] as const;

export const activityReferralKey = (referralId: string) =>
  ["activity-referral", referralId] as const;

export const activityYardSignPhotoKey = (
  businessId?: string | null,
  photoId?: string | null,
) => ["activity-yard-sign-photo", businessId, photoId] as const;

export const activityPricebookItemKey = (invoiceItemId: string) =>
  ["activity-pricebook-item", invoiceItemId] as const;

/**
 * Hook to fetch activity feed for a business with enriched data
 */
export const useGetActivityFeed = (
  businessId?: string | null,
  filters: ActivityFeedQueryFilters = {},
) => {
  const api = useApi();
  const { data: currentUser } = useGetMyUser();
  const normalizedFilters = normalizeActivityFeedQueryFilters(filters);

  return useInfiniteQuery({
    queryKey: activityFeedInfiniteKey(businessId, normalizedFilters),
    queryFn: ({ pageParam }) =>
      api
        .get(activityFeedItemsEndpoint(businessId as string), {
          params: buildActivityFeedRequestParams({
            myActivity: normalizedFilters.myActivity,
            page: pageParam,
            pageSize: normalizedFilters.pageSize,
            search: normalizedFilters.search,
            showUnattributedReviews: normalizedFilters.showUnattributedReviews,
            selectedFilters: normalizedFilters.selectedFilters,
          }),
        })
        .then((res) => res.data as IActivityFeedResponse)
        .then((data) => ({
          ...data,
          items: getEnrichedActivityFeedItems(data.items, currentUser),
        })),
    initialPageParam: 1,
    getNextPageParam: (lastPage: IEnrichedActivityFeedResponse) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    placeholderData: (previousData) => previousData,
    staleTime: ACTIVITY_FEED_STALE_TIME_MS,
  });
};

export const activityFeedHasUnattributedReviewsKey = (
  businessId?: string | null,
  myActivity?: boolean,
) =>
  [
    ...activityFeedKey(businessId),
    "has-unattributed-reviews",
    !!myActivity,
  ] as const;

export const useGetActivityFeedHasUnattributedReviews = (
  businessId?: string | null,
  myActivity = false,
) => {
  const api = useApi();

  return useQuery({
    queryKey: activityFeedHasUnattributedReviewsKey(businessId, myActivity),
    queryFn: () =>
      api
        .get(activityFeedItemsEndpoint(businessId as string), {
          params: buildActivityFeedRequestParams({
            myActivity,
            page: 1,
            pageSize: 1,
            showUnattributedReviews: true,
          }),
        })
        .then((res) => res.data as IActivityFeedResponse)
        .then((data) => data.items.length > 0),
    enabled: !!businessId,
    staleTime: ACTIVITY_FEED_STALE_TIME_MS,
  });
};

export const activityFeedEventsKey = (
  businessId?: string | null,
  myActivity = false,
) => [...activityFeedKey(businessId), "events", myActivity] as const;

export const useGetActivityFeedEvents = (
  businessId?: string | null,
  myActivity = false,
) => {
  const api = useApi();

  return useQuery({
    queryKey: activityFeedEventsKey(businessId, myActivity),
    queryFn: () =>
      api
        .get(activityFeedEventsEndpoint(businessId as string), {
          params: buildActivityFeedRequestParams({ myActivity }),
        })
        .then((res) => res.data as IActivityFeedEventOption[]),
    enabled: !!businessId,
    staleTime: ACTIVITY_FEED_STALE_TIME_MS,
  });
};

/**
 * Hook to fetch individual customer details for activity feed items
 */
export const useGetActivityCustomer = (
  customerId?: string | null,
  enabled = true,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: activityCustomerKey(customerId || ""),
    queryFn: () =>
      get(`/customers/${customerId}`).then(
        (res) => getEnrichedCustomer(res.data) as ICustomer,
      ),
    enabled: !!customerId && enabled,
  });
};

/**
 * Hook to fetch individual referral details for activity feed items
 */
export const useGetActivityReferral = (
  referralId?: string | null,
  enabled = true,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: activityReferralKey(referralId || ""),
    queryFn: () =>
      get(`/referrals/${referralId}`).then(
        (res) => getEnrichedReferral(res.data) as IReferral,
      ),
    enabled: !!referralId && enabled,
  });
};

/**
 * Hook to fetch individual yard sign photo details for activity feed items
 */
export const useGetActivityYardSignPhoto = (
  businessId?: string | null,
  photoId?: string | null,
  enabled = true,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: activityYardSignPhotoKey(businessId, photoId),
    queryFn: () =>
      get(`businesses/${businessId}/spiff_photos`, {
        params: { ids: [photoId] },
      }).then((res) => res.data[0] as PhotoTrigger),
    enabled: !!businessId && !!photoId && enabled,
  });
};

/**
 * Hook to fetch individual pricebook item details for activity feed items
 */
export const useGetActivityPricebookItem = (
  invoiceItemId?: string | null,
  enabled = true,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: activityPricebookItemKey(invoiceItemId || ""),
    queryFn: () =>
      get(`/service_titan_invoice_items/${invoiceItemId}`).then(
        (res) => res.data as IPricebookItem,
      ),
    enabled: !!invoiceItemId && enabled,
  });
};

/**
 * Helper to determine which record type to fetch based on event name
 */
export const getRecordTypeForEvent = (
  eventName: ActivityEventName,
): RecordType | null => {
  const spiffEventName = getSpiffEventName(eventName);
  return (
    EVENT_TO_RECORD_TYPE_MAP[
      spiffEventName as keyof typeof EVENT_TO_RECORD_TYPE_MAP
    ] || null
  );
};
/**
 * Hook to update review technicians (attribution)
 */
export const useUpdateReviewUsers = (businessId?: string | null) => {
  const { patch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reviewId,
      userIds,
      createSpiffs = false,
      approveSpiffs = false,
    }: {
      reviewId: string;
      userIds: string[];
      createSpiffs?: boolean;
      approveSpiffs?: boolean;
    }) =>
      patch(`/reviews/${reviewId}`, {
        business_id: businessId,
        technician_ids: userIds,
        create_spiffs: createSpiffs.toString(),
        approve_spiffs: approveSpiffs.toString(),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityFeedKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: businessReviewsKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: reviewKey(variables.reviewId),
      });
    },
  });
};
